import api from './api';

/**
 * Migration service for managing data imports
 */
const migrationService = {
  /**
   * Create and start a new migration job
   * @param {Object} data - Migration configuration
   * @param {string} data.source_type - Source system (clio, mycase)
   * @param {Object} data.source_config - Source configuration
   * @param {boolean} data.include_documents - Whether to import documents
   * @param {boolean} data.dry_run - Preview without committing
   * @returns {Promise<Object>} - Job ID and status
   */
  createMigration: async (data) => {
    const response = await api.post('/migrations', data);
    return response.data;
  },

  /**
   * List migration jobs for the current firm
   * @param {number} page - Page number
   * @param {number} pageSize - Items per page
   * @param {string} statusFilter - Optional status filter
   * @returns {Promise<Object>} - Paginated list of jobs
   */
  listMigrations: async (page = 1, pageSize = 20, statusFilter = null) => {
    const params = { page, page_size: pageSize };
    if (statusFilter) params.status_filter = statusFilter;
    const response = await api.get('/migrations', { params });
    return response.data;
  },

  /**
   * Get details of a specific migration job
   * @param {string} jobId - UUID of the job
   * @returns {Promise<Object>} - Job details with errors and warnings
   */
  getMigration: async (jobId) => {
    const response = await api.get(`/migrations/${jobId}`);
    return response.data;
  },

  /**
   * Get real-time progress of a migration job
   * @param {string} jobId - UUID of the job
   * @returns {Promise<Object>} - Progress information
   */
  getMigrationProgress: async (jobId) => {
    const response = await api.get(`/migrations/${jobId}/progress`);
    return response.data;
  },

  /**
   * Cancel a running migration job
   * @param {string} jobId - UUID of the job
   * @returns {Promise<Object>} - Cancellation result
   */
  cancelMigration: async (jobId) => {
    const response = await api.post(`/migrations/${jobId}/cancel`);
    return response.data;
  },

  /**
   * Retry a failed migration job
   * @param {string} jobId - UUID of the job
   * @returns {Promise<Object>} - New job ID and status
   */
  retryMigration: async (jobId) => {
    const response = await api.post(`/migrations/${jobId}/retry`);
    return response.data;
  },

  getPendingLinks: async () => {
    const response = await api.get('/migrations/pending-links');
    return response.data;
  },

  resolvePendingLink: async (linkId, chosenCaseId) => {
    const response = await api.post(`/migrations/pending-links/${linkId}/resolve`, {
      chosen_case_id: chosenCaseId,
    });
    return response.data;
  }
};

export default migrationService;
